import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import IconButton from 'components/@extended/IconButton';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// Constants
const TARGETS = {
  SELF: '_self',
  BLANK: '_blank'
};

const Z_INDEX = {
  LIST_ITEM: 1201,
  ACTION_BUTTON: 1202
};

const DRAWER_CONFIG = {
  LEVEL_MULTIPLIER: 28,
  ICON_SIZE_CLOSED: '1.25rem',
  ICON_SIZE_OPEN: '1rem',
  PARENT_ICON_SIZE: 20,
  PARENT_STROKE: '1.5'
};

const ACTION_BUTTON_CONFIG = {
  WIDTH: 20,
  HEIGHT: 20,
  TOP: 12,
  RIGHT: 20,
  ICON_SIZE: '0.625rem'
};

// Helper function to determine if content should be shown
const shouldShowContent = (drawerOpen, level) => drawerOpen || (!drawerOpen && level !== 1);

// Helper function to get item target
const getItemTarget = (item) => (item.target ? TARGETS.BLANK : TARGETS.SELF);

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level, isParents = false, setSelectedID }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const itemTarget = getItemTarget(item);

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);

    if (isParents && setSelectedID) {
      setSelectedID(item.id);
    }
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? DRAWER_CONFIG.ICON_SIZE_OPEN : DRAWER_CONFIG.ICON_SIZE_CLOSED,
        ...(isParents && {
          fontSize: DRAWER_CONFIG.PARENT_ICON_SIZE,
          stroke: DRAWER_CONFIG.PARENT_STROKE
        })
      }}
    />
  ) : (
    false
  );

  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link || item.url, end: false }, pathname);

  const textColor = 'text.primary';
  const iconSelectedColor = 'primary.main';
  const showContent = shouldShowContent(drawerOpen, level);

  return (
    <Box sx={{ position: 'relative' }}>
      <ListItemButton
        component={Link}
        to={item.url}
        target={itemTarget}
        disabled={item.disabled}
        selected={isSelected}
        sx={(theme) => ({
          zIndex: Z_INDEX.LIST_ITEM,
          pl: drawerOpen ? `${level * DRAWER_CONFIG.LEVEL_MULTIPLIER}px` : 1.5,
          py: !drawerOpen && level === 1 ? 1.25 : 1,
          ...(drawerOpen && {
            '&:hover': { bgcolor: 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'divider' }) },
            '&.Mui-selected': {
              bgcolor: 'primary.lighter',
              ...theme.applyStyles('dark', { bgcolor: 'divider' }),
              borderRight: '2px solid',
              borderColor: 'primary.main',
              color: iconSelectedColor,
              '&:hover': { color: iconSelectedColor, bgcolor: 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'divider' }) }
            }
          }),
          ...(!drawerOpen && {
            '&:hover': { bgcolor: 'transparent' },
            '&.Mui-selected': { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' }
          })
        })}
        onClick={() => itemHandler()}
      >
        {itemIcon && (
          <ListItemIcon
            sx={(theme) => ({
              minWidth: 28,
              color: isSelected ? iconSelectedColor : textColor,
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': { bgcolor: 'secondary.lighter', ...theme.applyStyles('dark', { bgcolor: 'secondary.light' }) }
              }),
              ...(!drawerOpen &&
                isSelected && {
                  bgcolor: 'primary.lighter',
                  ...theme.applyStyles('dark', { bgcolor: 'primary.900' }),
                  '&:hover': { bgcolor: 'primary.lighter', ...theme.applyStyles('dark', { bgcolor: 'primary.darker' }) }
                })
            })}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {showContent && (
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                {item.title}
              </Typography>
            }
          />
        )}
        {showContent && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
          />
        )}
      </ListItemButton>
      {showContent &&
        item?.actions?.map((action, index) => {
          const ActionIcon = action.icon;
          const callAction = action?.function;
          return (
            <IconButton
              key={action.url || action.type || index}
              {...(action.type === 'function' && {
                onClick: (event) => {
                  event.stopPropagation();
                  callAction();
                }
              })}
              {...(action.type === 'link' && {
                component: Link,
                to: action.url,
                target: action.target ? '_blank' : '_self'
              })}
              color="secondary"
              variant="outlined"
              sx={{
                position: 'absolute',
                top: ACTION_BUTTON_CONFIG.TOP,
                right: ACTION_BUTTON_CONFIG.RIGHT,
                zIndex: Z_INDEX.ACTION_BUTTON,
                width: ACTION_BUTTON_CONFIG.WIDTH,
                height: ACTION_BUTTON_CONFIG.HEIGHT,
                mr: -1,
                ml: 1,
                color: 'secondary.dark',
                borderColor: isSelected ? 'primary.light' : 'secondary.light',
                '&:hover': { borderColor: isSelected ? 'primary.main' : 'secondary.main' }
              }}
            >
              <ActionIcon style={{ fontSize: ACTION_BUTTON_CONFIG.ICON_SIZE }} />
            </IconButton>
          );
        })}
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
